import { ShieldCheck, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CONTACT } from "@/lib/contacts";

const EXCHANGES = [
  {
    name: "Binance",
    tag: "Top Global",
    logo: "/assets/binance.svg",
    accent: "#F3BA2F",
    url: "https://accounts.bmwweb.biz/register?ref=XPRRYQUJ",
  },
  {
    name: "Bitget",
    tag: "Derivatives",
    logo: "/assets/bitget.svg",
    accent: "#00F0FF",
    url: "https://partner.bitgetapp.com/bg/GUWAX2",
  },
  {
    name: "OKX",
    tag: "World-Class",
    logo: "/assets/okx.svg",
    accent: "#FFFFFF",
    url: "https://okx.ac/join/AIOTRADE20",
  },
  {
    name: "Tokocrypto",
    tag: "Indonesia",
    logo: "/assets/tokocrypto.svg",
    accent: "#1B43F0",
    url: "https://www.tokocrypto.com/app?scope=register&ref=A35985B5",
  },
];

const Exchanges = () => {
  const { t } = useLanguage();

  return (
    <section
      id="exchanges"
      aria-labelledby="exchanges-heading"
      data-testid="exchanges-section"
      className="relative py-20 md:py-28 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <header className="lg:col-span-4">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-[var(--accent2)] glow-accent">
            {t.exchanges.kicker}
          </p>
          <h2
            id="exchanges-heading"
            className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white"
          >
            {t.exchanges.title}
          </h2>
          <p className="mt-5 text-zinc-200 leading-relaxed text-base md:text-lg">
            {t.exchanges.desc}
          </p>
          <p className="mt-6 inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[rgb(var(--brand-rgb)/0.3)] bg-[rgb(var(--brand-rgb)/0.05)] text-xs md:text-sm text-[var(--brand)] font-semibold">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            {t.exchanges.legal}
          </p>
          <p className="mt-4">
            <a
              href={CONTACT.bappebtiCheck}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bappebti-check-link"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-[var(--brand)] transition-colors"
            >
              ceklegalitas.bappebti.go.id
              <span className="sr-only"> {t.a11y.external}</span>
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </p>
        </header>

        <ul className="lg:col-span-8 grid grid-cols-2 gap-4 min-w-0">
          {EXCHANGES.map((ex, i) => (
            <li key={ex.name} className="min-w-0">
              <a
                href={ex.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`exchange-card-${i}`}
                className="group relative block h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[rgb(var(--brand-rgb)/0.4)] hover:-translate-y-0.5 transition-all overflow-hidden"
              >
                <span
                  className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-0 group-hover:opacity-25 transition-opacity blur-2xl"
                  style={{ background: ex.accent }}
                  aria-hidden="true"
                />
                <span className="relative flex items-center justify-between gap-3">
                  <span className="flex h-12 md:h-14 items-center min-w-0 flex-1">
                    <img
                      src={ex.logo}
                      alt=""
                      aria-hidden="true"
                      width="120"
                      height="40"
                      className="max-h-full max-w-full w-auto object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                  <ExternalLink
                    className="shrink-0 h-4 w-4 text-zinc-300 group-hover:text-[var(--brand)] transition-colors"
                    aria-hidden="true"
                  />
                </span>
                <span className="relative mt-5 block font-heading text-xl md:text-2xl font-black tracking-tight text-white break-words">
                  {ex.name}
                </span>
                <span className="relative mt-1 block text-xs uppercase tracking-[0.2em] text-zinc-300">
                  {ex.tag}
                </span>
                <span className="sr-only"> {t.a11y.external}</span>
                <span
                  className="relative mt-6 block h-1.5 w-12 rounded-full"
                  style={{ background: ex.accent }}
                  aria-hidden="true"
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Exchanges;
